import { ProviderCustomFields } from '../types/practices/types';
import { getCustomFieldValue } from './customFieldUtils';

export const CATEGORY_MAPPING: Record<
  string,
  'Scheduling' | 'Treatments'
> = {
  'Next-In-Line': 'Scheduling',
  'Takes-Walk-Ins': 'Scheduling',
  'Cross-Coverage': 'Scheduling',
  'Cross-Coverage-Sick': 'Scheduling',
  'Cross-Coverage-for-Sick-Market': 'Scheduling',
  'Closest-Urgent-Care': 'Scheduling',
  'Type-Of-Schedule': 'Scheduling',
  'New-Patient': 'Scheduling',
  Age: 'Scheduling',
  'New-Telehealth': 'Scheduling',
  'Medicare-Wellness': 'Scheduling',
  'Flu-Shots': 'Treatments',
  'Other-Languages': 'Treatments',
  'Unvaccinated-Children': 'Treatments',
  'ADD-ADHD': 'Treatments',
  'Chronic-Pain-Management': 'Treatments',
  Pap: 'Treatments',
  'OB-Visits': 'Treatments',
  'Workers-Comp': 'Treatments',
  'Disability-Physical': 'Treatments',
  'Military-Physical': 'Treatments',
  'Flight-Physical': 'Treatments',
  'DOT-Physical': 'Treatments',
  'Immigration-Physical': 'Treatments',
  'Provider-Referral-Required': 'Scheduling',
  'HMO-LT3D-Request': 'Scheduling',
  'Auto-Accident-Related-Injuries': 'Treatments',
  'Same-Day': 'Scheduling',
  'Virtual-Waiting-Room': 'Scheduling',
  'Procedures-Surgeries': 'Treatments',
  'Conditions-Treated': 'Treatments',
  'Conditions-Not-Treated': 'Treatments',
  'Additional-Location-Days': 'Scheduling',
  Ekg: 'Treatments',
  'Out-Of-State-Telehealth': 'Scheduling',
  'Additional-Treatments-Offered': 'Treatments',
  'Facilities-Used': 'Treatments',
  'Patients-Virtual-Waiting-Schedule': 'Scheduling',
  'Provider-Group': 'Scheduling',
  'Hospital-Backline': 'Scheduling',
  'Cross-Schedule-Hospital-Follow-Up': 'Scheduling',
  'Scheduling-Notes': 'Scheduling',
  'State-Specific-Limitations': 'Treatments',
  'Supervision-Physician': 'Scheduling',
  Telehealth: 'Scheduling',
  // Note: Triage-Triggers is intentionally excluded as it's displayed outside columns
};

export const SCHEDULING_FIELDS: string[] = [
  'Additional-Location-Days',
  'Age',
  'Cross-Coverage',
  'Cross-Coverage-Sick',
  'Cross-Coverage-for-Sick-Market',
  'Closest-Urgent-Care',
  'HMO-LT3D-Request',
  'Medicare-Wellness',
  'New-Patient',
  'New-Telehealth',
  'Next-In-Line',
  'Out-Of-State-Telehealth',
  'Patients-Virtual-Waiting-Schedule',
  'Provider-Group',
  'Hospital-Backline',
  'Cross-Schedule-Hospital-Follow-Up',
  'Provider-Referral-Required',
  'Same-Day',
  'Scheduling-Notes',
  'Supervision-Physician',
  'Takes-Walk-Ins',
  'Telehealth',
  'Type-Of-Schedule',
  'Virtual-Waiting-Room',
];

export const TREATMENT_FIELDS: string[] = [
  'ADD-ADHD',
  'Additional-Treatments-Offered',
  'Auto-Accident-Related-Injuries',
  'Chronic-Pain-Management',
  'Conditions-Not-Treated',
  'Conditions-Treated',
  'Disability-Physical',
  'DOT-Physical',
  'Ekg',
  'Facilities-Used',
  'Flight-Physical',
  'Flu-Shots',
  'Immigration-Physical',
  'Military-Physical',
  'OB-Visits',
  'Other-Languages',
  'Pap',
  'Procedures-Surgeries',
  'State-Specific-Limitations',
  'Unvaccinated-Children',
  'Workers-Comp',
];

export const RICH_TEXT_FIELDS = [
  'Conditions-Treated',
  'Conditions-Not-Treated',
  'Additional-Location-Days',
  'Additional-Treatments-Offered',
  'Triage-Triggers',
  'Scheduling-Notes',
] as const;

export function normalizeFieldValue(input: unknown): string {
  if (input == null) return '';
  if (typeof input === 'string') return input;
  if (typeof input === 'number' || typeof input === 'boolean')
    return String(input);
  if (Array.isArray(input))
    return input.map(normalizeFieldValue).filter(Boolean).join(', ');
  if (typeof input === 'object' && input !== null) {
    const obj = input as Record<string, unknown>;
    if ('label' in obj || 'value' in obj) {
      const label = obj.label ?? obj.value;
      return normalizeFieldValue(label);
    }
    const primitives = Object.values(obj).filter((v) =>
      ['string', 'number', 'boolean'].includes(typeof v)
    );
    if (primitives.length)
      return primitives.map(normalizeFieldValue).join(', ');
    try {
      return JSON.stringify(input);
    } catch {
      return '';
    }
  }
  return '';
}

export const buildServices = (
  customFields: ProviderCustomFields | undefined
) => {
  const predefined = Object.keys(CATEGORY_MAPPING);
  return predefined.map((key) => {
    // For special fields that have complex structures, handle them specially
    if (
      key === 'Cross-Coverage-Sick' ||
      key === 'Cross-Coverage-for-Sick-Market' ||
      key === 'Closest-Urgent-Care' ||
      key === 'Cross-Coverage' ||
      key === 'Medicare-Wellness' ||
      key === 'Supervision-Physician' ||
      key === 'New-Telehealth' ||
      key === 'Out-Of-State-Telehealth' ||
      key === 'Telehealth'
    ) {
      // These fields are handled by special badges/components, so don't process them here
      // They read directly from customFields in the UI components
      return {
        name: key,
        detail: '', // Empty detail since badges/components handle display
        category: CATEGORY_MAPPING[key] || ('Other' as const),
      };
    }

    const extracted = getCustomFieldValue(customFields, key);

    if (RICH_TEXT_FIELDS.includes(key as (typeof RICH_TEXT_FIELDS)[number])) {
      // Prefer metadata.htmlContent (new format); fall back to value (old format, may be HTML)
      const cfArr = customFields?.[key] as
        | { metadata?: { htmlContent?: string } }[]
        | undefined;
      const htmlContent =
        cfArr?.[0]?.metadata?.htmlContent || extracted;
      if (typeof htmlContent === 'string' && htmlContent.trim() !== '') {
        return {
          name: key,
          detail: htmlContent,
          category: CATEGORY_MAPPING[key] || ('Other' as const),
          isRichText: true,
        };
      }
    }

    const detail = normalizeFieldValue(extracted);
    return {
      name: key,
      detail,
      category: CATEGORY_MAPPING[key] || ('Other' as const),
    };
  });
};

/**
 * Client ask: keep the coverage fields together and adjacent, without changing
 * the relative order of the other scheduling fields.
 * PCP order: Takes-Walk-Ins, Cross-Coverage-Sick (in clinic), Closest-Urgent-Care,
 * Cross-Coverage-for-Sick-Market, Next-In-Line.
 * Note: every field in the PCP group except Cross-Coverage-Sick is PCP-only.
 */
export const getSchedulingGroupOrder = (isPCP: boolean): string[] =>
  isPCP
    ? [
        'Takes-Walk-Ins',
        'Cross-Coverage-Sick',
        'Closest-Urgent-Care',
        'Cross-Coverage-for-Sick-Market',
        'Next-In-Line',
      ]
    : ['Cross-Coverage-Sick', 'Cross-Coverage'];

/**
 * Moves the coverage group into one adjacent block, positioned where the first
 * of its members already appeared. Fields outside the group keep their order.
 */
export const applySchedulingGroupOrder = <T extends { name: string }>(
  fields: T[],
  isPCP: boolean
): T[] => {
  const groupOrder = getSchedulingGroupOrder(isPCP);
  const groupedItems = groupOrder
    .map((name) => fields.find((field) => field.name === name))
    .filter((field): field is T => Boolean(field));

  if (groupedItems.length === 0) {
    return fields;
  }

  const isGroupName = (name: string) => groupOrder.includes(name);
  const reordered: T[] = [];
  let inserted = false;
  for (const field of fields) {
    if (!inserted && isGroupName(field.name)) {
      reordered.push(...groupedItems);
      inserted = true;
    }
    if (!isGroupName(field.name)) {
      reordered.push(field);
    }
  }
  return reordered;
};
